import { EGG_BY_ID, ENEMY_BY_ID, WEAPON_BY_ID } from "../core/content";
import { localizeEnemyName } from "../content-i18n";
import { drawArenaAsset, drawAtlasSprite } from "./assets";
import type { Locale } from "../i18n";
import type {
  Arena,
  BattleEvent,
  BattleState,
  Vec2,
} from "../core/types";

interface Particle {
  position: Vec2;
  velocity: Vec2;
  color: string;
  radius: number;
  life: number;
}

export interface RenderPreferences {
  screenShake: boolean;
  reducedEffects: boolean;
  locale?: Locale;
}

export interface CameraTransform {
  mode: "full-arena" | "portrait-follow";
  scale: number;
  offsetX: number;
  offsetY: number;
}

export function calculateCamera(
  arena: Arena,
  playerPosition: Vec2,
  viewWidth: number,
  viewHeight: number,
): CameraTransform {
  const portrait = viewWidth / viewHeight < 0.72;
  const scale = portrait
    ? Math.max(viewWidth / arena.width, viewHeight / arena.height)
    : Math.min(viewWidth / arena.width, viewHeight / arena.height);

  if (!portrait) {
    return {
      mode: "full-arena",
      scale,
      offsetX: (viewWidth - arena.width * scale) / 2,
      offsetY: (viewHeight - arena.height * scale) / 2,
    };
  }

  const visibleWidth = viewWidth / scale;
  const visibleHeight = viewHeight / scale;
  const cameraX = clamp(
    playerPosition.x,
    visibleWidth / 2,
    arena.width - visibleWidth / 2,
  );
  const cameraY = clamp(
    playerPosition.y,
    visibleHeight / 2,
    arena.height - visibleHeight / 2,
  );

  return {
    mode: "portrait-follow",
    scale,
    offsetX: viewWidth / 2 - cameraX * scale,
    offsetY: viewHeight / 2 - cameraY * scale,
  };
}

export class BattleRenderer {
  readonly #canvas: HTMLCanvasElement;
  readonly #context: CanvasRenderingContext2D;
  #particles: Particle[] = [];
  #shake = 0;
  #pixelRatio = 1;
  #locale: Locale;
  readonly #preferences: RenderPreferences;

  constructor(
    canvas: HTMLCanvasElement,
    preferences: RenderPreferences = {
      screenShake: true,
      reducedEffects: false,
    },
  ) {
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas 2D is not supported.");
    this.#canvas = canvas;
    this.#context = context;
    this.#preferences = preferences;
    this.#locale = preferences.locale ?? "en";
  }

  setLocale(locale: Locale): void {
    this.#locale = locale;
  }

  consume(events: BattleEvent[]): void {
    for (const event of events) {
      if (event.type === "enemy-hit") {
        this.#burst(event.x, event.y, "#fff3d6", 3, 4);
      }
      if (event.type === "critical-hit") {
        this.#burst(event.x, event.y, "#ffd60a", 14, 9);
      }
      if (event.type === "enemy-killed") {
        this.#burst(
          event.x,
          event.y,
          ENEMY_BY_ID.get(event.kind)?.color ?? "#ef5d60",
          10,
          8,
        );
      }
      if (event.type === "player-hurt") {
        if (this.#preferences.screenShake) {
          this.#shake = Math.min(16, this.#shake + 8);
        }
      }
      if (event.type === "level-up") {
        this.#burst(800, 450, "#ffb703", 20, 12);
      }
      if (event.type === "wave-cleared") {
        this.#burst(800, 450, "#47b8b7", 36, 15);
      }
    }
  }

  render(state: BattleState): void {
    this.#resize();
    const context = this.#context;
    const viewWidth = this.#canvas.clientWidth;
    const viewHeight = this.#canvas.clientHeight;
    const { mode, scale, offsetX, offsetY } = calculateCamera(
      state.arena,
      state.player.position,
      viewWidth,
      viewHeight,
    );
    this.#canvas.dataset.camera = mode;
    const shakeX = this.#shake > 0 ? (Math.random() - 0.5) * this.#shake : 0;
    const shakeY = this.#shake > 0 ? (Math.random() - 0.5) * this.#shake : 0;
    this.#shake *= 0.82;

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, this.#canvas.width, this.#canvas.height);
    context.setTransform(
      scale * this.#pixelRatio,
      0,
      0,
      scale * this.#pixelRatio,
      (offsetX + shakeX) * this.#pixelRatio,
      (offsetY + shakeY) * this.#pixelRatio,
    );
    this.#drawArena(state);
    this.#drawPickups(state);
    this.#drawProjectiles(state);
    this.#drawEnemies(state);
    this.#drawEquippedWeapons(state);
    this.#drawPlayer(state);
    this.#drawParticles();
  }

  #resize(): void {
    this.#pixelRatio = Math.min(2, window.devicePixelRatio || 1);
    const width = Math.round(this.#canvas.clientWidth * this.#pixelRatio);
    const height = Math.round(this.#canvas.clientHeight * this.#pixelRatio);
    if (this.#canvas.width === width && this.#canvas.height === height) return;
    this.#canvas.width = width;
    this.#canvas.height = height;
  }

  #drawArena(state: BattleState): void {
    const context = this.#context;
    if (drawArenaAsset(context, state.arena.width, state.arena.height)) {
      context.fillStyle = "rgba(7, 9, 13, .1)";
      context.fillRect(0, 0, state.arena.width, state.arena.height);
      return;
    }
    const gradient = context.createRadialGradient(800, 450, 80, 800, 450, 900);
    gradient.addColorStop(0, "#262934");
    gradient.addColorStop(1, "#171922");
    context.fillStyle = gradient;
    context.fillRect(0, 0, state.arena.width, state.arena.height);

    context.strokeStyle = "rgba(255,243,214,.045)";
    context.lineWidth = 2;
    for (let x = 0; x <= state.arena.width; x += 80) {
      context.beginPath();
      context.moveTo(x, 0);
      context.lineTo(x, state.arena.height);
      context.stroke();
    }
    for (let y = 0; y <= state.arena.height; y += 80) {
      context.beginPath();
      context.moveTo(0, y);
      context.lineTo(state.arena.width, y);
      context.stroke();
    }
    context.strokeStyle = "rgba(255,183,3,.15)";
    context.lineWidth = 8;
    context.strokeRect(4, 4, state.arena.width - 8, state.arena.height - 8);
  }

  #drawPlayer(state: BattleState): void {
    const { position, radius, invulnerableTicks, health, maxHealth } =
      state.player;
    const egg = EGG_BY_ID.get(state.eggId);
    const context = this.#context;
    const flicker = invulnerableTicks > 0 && Math.floor(invulnerableTicks / 3) % 2;
    if (flicker) context.globalAlpha = 0.42;

    context.save();
    context.translate(position.x, position.y + 3);
    context.scale(0.86, 1.08);
    context.fillStyle = "rgba(0,0,0,.28)";
    context.beginPath();
    context.ellipse(3, radius + 14, radius * 0.9, 10, 0, 0, Math.PI * 2);
    context.fill();

    if (
      drawAtlasSprite(
        context,
        "egg",
        state.eggId,
        0,
        -radius * 0.2,
        radius * 4.45,
        radius * 4.45,
      )
    ) {
      const healthRatio = health / maxHealth;
      if (healthRatio < 0.67) {
        context.strokeStyle = "#4b263d";
        context.lineWidth = 2.8;
        context.beginPath();
        context.moveTo(-4, -25);
        context.lineTo(-10, -13);
        context.lineTo(-2, -6);
        if (healthRatio < 0.34) {
          context.lineTo(-12, 6);
          context.moveTo(-2, -6);
          context.lineTo(8, 2);
        }
        context.stroke();
      }
      context.restore();
      context.globalAlpha = 1;
      return;
    }

    context.fillStyle = "#fff3d6";
    context.strokeStyle = egg?.accent ?? "#d6b98a";
    context.lineWidth = 6;
    context.beginPath();
    context.ellipse(0, 0, radius, radius, 0, 0, Math.PI * 2);
    context.fill();
    context.stroke();

    context.fillStyle = "#292633";
    context.beginPath();
    context.ellipse(-9, -3, 3.4, 5, 0, 0, Math.PI * 2);
    context.ellipse(9, -3, 3.4, 5, 0, 0, Math.PI * 2);
    context.fill();
    context.strokeStyle = "#292633";
    context.lineWidth = 3;
    context.beginPath();
    const healthRatio = health / maxHealth;
    if (healthRatio > 0.67) {
      context.moveTo(-6, 8);
      context.lineTo(6, 8);
    } else if (healthRatio > 0.33) {
      context.arc(0, 13, 7, Math.PI + 0.2, Math.PI * 2 - 0.2);
    } else {
      context.ellipse(0, 10, 6, 8, 0, 0, Math.PI * 2);
    }
    context.stroke();

    if (healthRatio < 0.67) {
      context.strokeStyle = "#8f765c";
      context.lineWidth = 2.5;
      context.beginPath();
      context.moveTo(-4, -25);
      context.lineTo(-10, -13);
      context.lineTo(-2, -6);
      if (healthRatio < 0.34) {
        context.lineTo(-12, 6);
        context.moveTo(-2, -6);
        context.lineTo(8, 2);
      }
      context.stroke();
    }
    context.restore();
    context.globalAlpha = 1;
  }

  #drawEquippedWeapons(state: BattleState): void {
    const context = this.#context;
    const weapons = state.weapons.slice(0, state.player.weaponSlotLimit);
    if (weapons.length === 0) return;

    let target: BattleState["enemies"][number] | undefined;
    let targetDistance = Number.POSITIVE_INFINITY;
    for (const enemy of state.enemies) {
      if (ENEMY_BY_ID.get(enemy.kind)?.rank === "neutral") continue;
      const distance =
        (enemy.position.x - state.player.position.x) ** 2 +
        (enemy.position.y - state.player.position.y) ** 2;
      if (distance < targetDistance) {
        target = enemy;
        targetDistance = distance;
      }
    }
    const aimAngle = target
      ? Math.atan2(
          target.position.y - state.player.position.y,
          target.position.x - state.player.position.x,
        )
      : Math.atan2(state.player.velocity.y, state.player.velocity.x || 1);

    weapons.forEach((weapon, index) => {
      const definition = WEAPON_BY_ID.get(weapon.definitionId);
      const slotOffset =
        weapons.length === 1
          ? 0
          : (index - (weapons.length - 1) / 2) * Math.min(0.72, 3.2 / weapons.length);
      const mountAngle = aimAngle + slotOffset;
      const attackPulse = weapon.cooldownTicks <= 4 ? 9 : 0;
      const mountDistance = state.player.radius + 17 + attackPulse;
      const x = state.player.position.x + Math.cos(mountAngle) * mountDistance;
      const y = state.player.position.y + Math.sin(mountAngle) * mountDistance;
      const size = 42 + weapon.tier * 3;

      context.save();
      context.translate(x, y);
      context.rotate(mountAngle);
      context.shadowColor = definition?.color ?? "#fff3d6";
      context.shadowBlur = weapon.cooldownTicks <= 4 ? 14 : 5;
      if (
        !drawAtlasSprite(
          context,
          "weapon",
          weapon.definitionId,
          0,
          0,
          size,
          size,
        )
      ) {
        context.fillStyle = definition?.color ?? "#fff3d6";
        context.strokeStyle = "#201923";
        context.lineWidth = 3;
        context.beginPath();
        context.roundRect(-size * 0.42, -5, size * 0.84, 10, 5);
        context.fill();
        context.stroke();
      }
      context.restore();
    });
  }

  #drawEnemies(state: BattleState): void {
    const context = this.#context;
    for (const enemy of state.enemies) {
      const definition = ENEMY_BY_ID.get(enemy.kind);
      context.save();
      context.translate(enemy.position.x, enemy.position.y);
      const squish = definition?.behavior === "zigzag" ? 0.82 : 1;
      context.scale(1 / squish, squish);
      if (definition && definition.rank !== "normal") {
        context.strokeStyle =
          definition.rank === "boss" ? "#ffb703" : "#a8fff5";
        context.lineWidth = 3;
        context.beginPath();
        context.arc(0, 0, enemy.radius + 9, 0, Math.PI * 2);
        context.stroke();
      }
      const artworkSize =
        enemy.radius * (definition?.rank === "boss" ? 3.25 : 4.2);
      const drewArtwork =
        enemy.kind === "tree"
          ? drawAtlasSprite(
              context,
              "interface",
              "tree",
              0,
              -enemy.radius * 0.12,
              artworkSize,
              artworkSize,
            )
          : drawAtlasSprite(
              context,
              "enemy",
              enemy.kind,
              0,
              -enemy.radius * 0.12,
              artworkSize,
              artworkSize,
            );
      if (!drewArtwork) {
        context.fillStyle =
          enemy.hitFlashTicks > 0
            ? "#fff3d6"
            : (definition?.color ?? "#a64d74");
        context.strokeStyle = "#4b263d";
        context.lineWidth =
          definition?.rank === "boss" ? 11 : definition?.rank === "elite" ? 8 : 5;
        context.beginPath();
        context.arc(0, 0, enemy.radius, 0, Math.PI * 2);
        context.fill();
        context.stroke();
        context.fillStyle = "white";
        context.beginPath();
        context.arc(-enemy.radius * 0.3, -3, 4.4, 0, Math.PI * 2);
        context.arc(enemy.radius * 0.3, -3, 4.4, 0, Math.PI * 2);
        context.fill();
        context.fillStyle = "#221822";
        context.beginPath();
        context.arc(-enemy.radius * 0.28, -2, 2, 0, Math.PI * 2);
        context.arc(enemy.radius * 0.28, -2, 2, 0, Math.PI * 2);
        context.fill();
      } else if (enemy.hitFlashTicks > 0) {
        context.strokeStyle = "rgba(255, 243, 214, .88)";
        context.lineWidth = 5;
        context.beginPath();
        context.arc(0, 0, enemy.radius * 1.18, 0, Math.PI * 2);
        context.stroke();
      }
      context.restore();

    }

    const boss = state.enemies.find(
      (enemy) => ENEMY_BY_ID.get(enemy.kind)?.rank === "boss",
    );
    if (boss) {
      const definition = ENEMY_BY_ID.get(boss.kind);
      const width = 720;
      const x = (state.arena.width - width) / 2;
      context.fillStyle = "rgba(8,9,13,.78)";
      context.fillRect(x, 28, width, 30);
      context.fillStyle = definition?.color ?? "#ef5d60";
      context.fillRect(x + 5, 33, (width - 10) * (boss.health / boss.maxHealth), 20);
      context.fillStyle = "#fff3d6";
      context.font = "900 18px system-ui";
      context.textAlign = "center";
      context.fillText(
        definition ? localizeEnemyName(this.#locale, definition) : "BOSS",
        state.arena.width / 2,
        82,
      );
    }
  }

  #drawProjectiles(state: BattleState): void {
    const context = this.#context;
    for (const projectile of state.projectiles) {
      context.save();
      context.strokeStyle = projectile.color;
      context.globalAlpha = 0.25;
      context.lineWidth = projectile.radius * 2;
      context.beginPath();
      context.moveTo(
        projectile.position.x - projectile.velocity.x * 0.018,
        projectile.position.y - projectile.velocity.y * 0.018,
      );
      context.lineTo(projectile.position.x, projectile.position.y);
      context.stroke();
      context.globalAlpha = 1;
      context.fillStyle = projectile.color;
      context.translate(projectile.position.x, projectile.position.y);
      context.rotate(Math.atan2(projectile.velocity.y, projectile.velocity.x));
      context.beginPath();
      if (projectile.shape === "needle") {
        context.roundRect(
          -projectile.radius * 1.9,
          -projectile.radius * 0.42,
          projectile.radius * 3.8,
          projectile.radius * 0.84,
          projectile.radius * 0.4,
        );
      } else if (projectile.shape === "blade") {
        context.ellipse(
          0,
          0,
          projectile.radius * 1.35,
          projectile.radius * 0.62,
          0,
          0,
          Math.PI * 2,
        );
      } else {
        context.arc(0, 0, projectile.radius, 0, Math.PI * 2);
      }
      context.fill();
      if (projectile.shape === "orb") {
        context.strokeStyle = "#ffffff";
        context.globalAlpha = 0.55;
        context.lineWidth = 2;
        context.stroke();
      }
      context.restore();
    }
  }

  #drawPickups(state: BattleState): void {
    const context = this.#context;
    for (const pickup of state.pickups) {
      const spriteId =
        pickup.kind === "crate"
          ? pickup.value >= 4
            ? "crate-legendary"
            : "crate-normal"
          : pickup.kind;
      const color =
        pickup.kind === "material"
          ? "#47b8b7"
          : pickup.kind === "consumable"
            ? "#80ed99"
            : "#ffd166";
      context.save();
      context.translate(pickup.position.x, pickup.position.y);
      const pulse =
        pickup.kind === "material"
          ? 1 + Math.sin((state.tick + pickup.id * 7) * 0.12) * 0.12
          : 1;
      context.scale(pulse, pulse);
      context.shadowColor = color;
      context.shadowBlur = pickup.kind === "crate" ? 26 : pickup.kind === "material" ? 28 : 18;
      if (pickup.kind === "material") {
        context.fillStyle = "rgba(71, 184, 183, .2)";
        context.strokeStyle = "rgba(232, 255, 251, .82)";
        context.lineWidth = 3;
        context.beginPath();
        context.arc(0, 0, pickup.radius * 2.25, 0, Math.PI * 2);
        context.fill();
        context.stroke();
      }
      if (
        drawAtlasSprite(
          context,
          "interface",
          spriteId,
          0,
          0,
          pickup.radius * (pickup.kind === "crate" ? 4.6 : pickup.kind === "material" ? 4.8 : 3.8),
        )
      ) {
        context.restore();
        continue;
      }
      context.rotate(pickup.kind === "material" ? Math.PI / 4 : 0);
      context.fillStyle = color;
      context.strokeStyle = pickup.kind === "crate" ? "#fff4c2" : "#e8fffb";
      context.lineWidth = 3;
      if (pickup.kind === "consumable") {
        context.beginPath();
        context.arc(0, 0, pickup.radius, 0, Math.PI * 2);
        context.fill();
        context.stroke();
      } else {
        context.fillRect(-pickup.radius, -pickup.radius, pickup.radius * 2, pickup.radius * 2);
        context.strokeRect(-pickup.radius, -pickup.radius, pickup.radius * 2, pickup.radius * 2);
      }
      context.restore();
    }
    context.shadowBlur = 0;
  }

  #burst(
    x: number,
    y: number,
    color: string,
    count: number,
    radius: number,
  ): void {
    const adjustedCount = this.#preferences.reducedEffects
      ? Math.max(1, Math.ceil(count * 0.3))
      : count;
    for (let index = 0; index < adjustedCount; index += 1) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 70 + Math.random() * 190;
      this.#particles.push({
        position: { x, y },
        velocity: { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed },
        color,
        radius: radius * (0.5 + Math.random() * 0.7),
        life: 1,
      });
    }
    if (this.#particles.length > 240) {
      this.#particles.splice(0, this.#particles.length - 240);
    }
  }

  #drawParticles(): void {
    const context = this.#context;
    for (const particle of this.#particles) {
      particle.position.x += particle.velocity.x / 60;
      particle.position.y += particle.velocity.y / 60;
      particle.velocity.x *= 0.93;
      particle.velocity.y *= 0.93;
      particle.life -= 0.045;
      if (particle.life <= 0) continue;
      context.globalAlpha = Math.max(0, particle.life);
      context.fillStyle = particle.color;
      context.beginPath();
      context.arc(
        particle.position.x,
        particle.position.y,
        Math.max(0.01, particle.radius * particle.life),
        0,
        Math.PI * 2,
      );
      context.fill();
    }
    context.globalAlpha = 1;
    this.#particles = this.#particles.filter((particle) => particle.life > 0);
  }
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}
